
import React, {useRef } from "react";
import { useNavigate } from "react-router-dom";
import BadgeDownload from "./BadgeDownload";
import { ArrowUp } from "lucide-react";
import CompletionOverlay from "./CompletionOverlay";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";




export default function CompletionModal({ show, onClose, stats, name, difficulty }) {
  if (!show || !stats) return null;

  const playerName = name || localStorage.getItem("player-name") || "Explorer";
  const {
    gemsCollected = 0,
    playerPathLen = 0,
    shortestPathLen = 0,
    wallHits = 0,
    timeTaken = 0,
    score = 0,
    regularGems = Math.max(1, gemsCollected - 1),
    goldenGems = 1,
  } = stats;

  const baseScore = 1200;
  const completionBonus = 600;
  const gemBonus = gemsCollected * 80;
  const wallPenalty = wallHits * 5;
  const timePenalty = Math.max(0, (timeTaken - shortestPathLen) * 3);
  const extraStepPenalty = Math.max(0, (playerPathLen - shortestPathLen) * 3);

  const regularGemScore = regularGems * 10;
  const goldenGemScore = goldenGems * 200;
  const totalPossibleGems = regularGems + goldenGems;
  const collectionRate = totalPossibleGems > 0 ? Math.round((gemsCollected / totalPossibleGems) * 100) : 0;
  const collectionBonus = Math.round(collectionRate * 2);

  const pathEfficiency = playerPathLen / shortestPathLen;
  const speedPerStep = timeTaken / playerPathLen;
  const speedBonus = speedPerStep <= 2.0 ? 90 : speedPerStep <= 3.0 ? 50 : 0;

  const achievements = [];
  if (pathEfficiency <= 1.1) achievements.push({ icon: "🎯", name: "Perfect Path", desc: "Optimal route taken" });
  if (speedPerStep <= 1.0) achievements.push({ icon: "⚡", name: "Speed Runner", desc: "Lightning fast completion" });
  if (wallHits === 0) achievements.push({ icon: "👻", name: "Ghost Walker", desc: "No wall collisions" });
  if (gemsCollected === totalPossibleGems) achievements.push({ icon: "💎", name: "Treasure Hunter", desc: "All gems collected" });
  if (timeTaken <= 60) achievements.push({ icon: "⏰", name: "Time Master", desc: "Completed under 1 minute" });
  if (score >= 2500) achievements.push({ icon: "👑", name: "Champion", desc: "Exceptional performance" });
  if (difficulty === "hard" && score >= 1500) achievements.push({ icon: "🔥", name: "Hard Mode Hero", desc: "Conquered hard difficulty" });
  if (difficulty === "easy" && pathEfficiency <= 1.2) achievements.push({ icon: "🌟", name: "Efficient Explorer", desc: "Perfect beginner run" });
  if (goldenGems > 0) achievements.push({ icon: "⭐", name: "Golden Eye", desc: "Found golden gem" });
  if (playerPathLen <= shortestPathLen + 5) achievements.push({ icon: "🧭", name: "Navigator", desc: "Near-perfect navigation" });

  const playAgain = () => window.location.reload();
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const scrollToTop = () => {
  if (containerRef.current) {
    containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
const [showOverlay, setShowOverlay] = useState(true);
useEffect(() => {
  const timer = setTimeout(() => {
    setShowOverlay(false);
  }, 3000); // ⏳ Hide overlay after 3s
  return () => clearTimeout(timer);
}, []);

  return (
     <>
  {showOverlay && <CompletionOverlay playerName={name} score={score} stats={stats} onComplete={() => setShowOverlay(false)} />}

  {!showOverlay && (
    <motion.div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-gradient-to-b from-indigo-900 via-purple-800 to-pink-700 text-white overflow-y-auto"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 80, damping: 16 }}
    >

        {/* Floating Top Action Buttons */}
      <div
  className="absolute top-4 left-4 z-50 cursor-pointer"
  onClick={() => navigate("/")}
  title="Return to Home"
>
  <img src="/M-logo.png" alt="MazeMoh Logo" className="w-10 h-10" />
</div>
      <div className="fixed top-4 right-6 z-50">
        <button
          onClick={playAgain}
           className="bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold px-4 py-2 rounded-xl shadow-lg hover:scale-[1.03] transition-all duration-300 ease-in-out transform"
        >🚀 Play Again</button>
      </div>

      <div id="completion-top" className="max-w-6xl mx-auto pt-24 pb-12 px-6 min-h-screen flex flex-col justify-start">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold mb-2">Woohoo 🎉</h2>
          <p className="text-lg">Dear {playerName},</p>
        </div>

        {/* Badge */}
        <div className="relative flex justify-center mb-4 group animate-bounce-in">
          <div className="relative group">
            <BadgeDownload playerName={playerName} rank={stats?.rank || "Wanderer"} />
            
          </div>
        </div>

        {/* Final Score */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-semibold text-yellow-300">⭐ Final Score: {score.toLocaleString()}</h3>
        </div>

        {/* Summary Row */}
        <div className="grid grid-cols-4 text-center text-sm bg-white/10 p-4 rounded-lg mb-6">
          <div>
            <div className="text-purple-300 font-medium">{difficulty || "Medium"}</div>
            <div className="text-xs text-white-300">Difficulty</div>
          </div>
          <div>
            <div className="font-medium">{Math.floor(timeTaken / 60)}m {timeTaken % 60}s</div>
            <div className="text-xs text-white-300">Time</div>
          </div>
          <div>
            <div className="font-medium">{playerPathLen}</div>
            <div className="text-xs text-white-300">Steps</div>
            <div className="text-xs text-white-500">Optimal Steps: {shortestPathLen}</div>
          </div>
          <div>
            <div className="font-medium">{gemsCollected}</div>
            <div className="text-xs text-white-300">Gems</div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="bg-white/10 p-4 rounded-lg grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 text-sm gap-3 text-center mb-6">
          <div>+{baseScore} <div className="text-xs text-gray-300">🏁 Base</div></div>
          <div>+{completionBonus} <div className="text-xs text-gray-300">✅ Done</div></div>
          <div>+{gemBonus} <div className="text-xs text-gray-300">💎 Gems</div></div>
          <div>-{wallPenalty} <div className="text-xs text-gray-300">🧱 Walls</div></div>
          <div>-{timePenalty} <div className="text-xs text-gray-300">⏱️ Time</div></div>
          <div>-{extraStepPenalty} <div className="text-xs text-gray-300">👣 Steps</div></div>
        </div>

        {/* Performance + Treasure */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white/10 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-2">📊 Performance</h4>
            <p>🎯 Path Efficiency: {pathEfficiency.toFixed(2)}x</p>
            <p>⚡ Speed: {speedPerStep.toFixed(1)}s/step (+{speedBonus})</p>
          </div>
          <div className="bg-white/10 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-2">💎 Treasure</h4>
            <p>💠 Regular: +{regularGemScore}</p>
            <p>⭐ Golden: +{goldenGemScore}</p>
            <p>📊 Rate: {collectionRate}% (+{collectionBonus})</p>
          </div>
        </div>

        {/* Achievements */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3">🏆 Achievements Unlocked</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {achievements.map((ach, index) => (
              <div key={index} className="bg-white/10 p-3 rounded text-center">
                <div className="text-2xl mb-1">{ach.icon}</div>
                <div className="text-sm font-bold">{ach.name}</div>
                <div className="text-xs text-gray-300">{ach.desc}</div>
              </div>
            ))}
          </div>
        </div>

        
         
      </div>

      {/* Go to Top Icon */}
      <button
  onClick={scrollToTop}
  className="fixed bottom-8 right-6 rounded-full bg-pink-600 text-white shadow-lg hover:bg-indigo-700 transition z-50"
  aria-label="Go to Top"
>
  <ArrowUp className="w-5 h-5" />
</button>
</motion.div>
  )}
</>
  );
}